import type { AiWorkflowFile, TenantWorkbook, TenantWorkbookCell } from '@einwert/valuation-api-client'
import { read, utils } from 'xlsx'
import type { CellObject } from 'xlsx'

export const TENANT_WORKBOOK_MEDIA_TYPE = 'application/vnd.einwert.tenant-workbook+json'
export const MAX_TENANT_IMPORT_BYTES = 20 * 1024 * 1024
export const TENANT_IMPORT_FILE_TYPES = [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel.sheet.macroEnabled.12',
    '.xls',
    '.xlsx',
    '.xlsm',
]

export function readTenantWorkbook(data: ArrayBuffer, filename: string): TenantWorkbook {
    const source = read(data, { type: 'array', cellDates: true, cellFormula: true, cellText: true, cellStyles: true })
    const sheets = source.SheetNames.map((name, sheetIndex) => {
        const sheet = source.Sheets[name]
        const rows = new Map<number, TenantWorkbookCell[]>()
        for (const address of Object.keys(sheet)) {
            if (!/^[A-Z]+\d+$/.test(address)) {
                continue
            }
            const cell = sheet[address] as CellObject
            if (cell.v === undefined && !cell.f && !cell.c?.length) {
                continue
            }
            const position = utils.decode_cell(address)
            const number = position.r + 1
            if (!rows.has(number)) {
                rows.set(number, [])
            }
            const raw = cell.v
            const value = cell.t === 'e' ? null : raw instanceof Date ? raw.toISOString().slice(0, 10) : (raw ?? null)
            const comment = cell.c?.map((item) => item.t).join('\n')
            rows.get(number)?.push({
                column: utils.encode_col(position.c),
                value,
                ...(cell.w ? { formatted: cell.w } : {}),
                ...(cell.f ? { formula: cell.f } : {}),
                ...(comment ? { comment } : {}),
                ...(sheet['!cols']?.[position.c]?.hidden || sheet['!rows']?.[position.r]?.hidden ? { hidden: true } : {}),
            })
        }
        return {
            name,
            hidden: !!source.Workbook?.Sheets?.[sheetIndex]?.Hidden,
            merges: sheet['!merges']?.map((range) => utils.encode_range(range)) ?? [],
            rows: [...rows]
                .filter(([, cells]) =>
                    cells.some(
                        ({ value, formula, comment, formatted }) =>
                            (value !== null && value !== '') || comment || formatted || (formula && value === null)
                    )
                )
                .sort(([left], [right]) => left - right)
                .map(([number, cells]) => ({ number, cells })),
        }
    })
    return { version: 1, filename, sheets }
}

export async function tenantImportAttachment(file: File): Promise<AiWorkflowFile> {
    if (file.size > MAX_TENANT_IMPORT_BYTES) {
        throw new Error('Bitte eine Datei mit höchstens 20 MB auswählen.')
    }
    const bytes = await file.arrayBuffer()
    if (/\.pdf$/i.test(file.name)) {
        return {
            name: file.name,
            source: { sourceType: 'BYTE_CONTENT', byteContent: { data: base64(new Uint8Array(bytes)), mediaType: 'application/pdf' } },
        }
    }
    if (!/\.(xlsx|xlsm|xls)$/i.test(file.name)) {
        throw new Error('Bitte eine Excel-Datei (.xlsx, .xlsm, .xls) oder ein PDF auswählen.')
    }
    const workbook = readTenantWorkbook(bytes, file.name)
    return tenantWorkbookAttachment(workbook)
}

export function tenantWorkbookAttachment(workbook: TenantWorkbook): AiWorkflowFile {
    const snapshot = new TextEncoder().encode(JSON.stringify(workbook))
    if (snapshot.length > MAX_TENANT_IMPORT_BYTES) {
        throw new Error('Die ausgelesenen Tabellen überschreiten die Upload-Grenze von 20 MB.')
    }
    return {
        name: `${workbook.filename.split('/').at(-1)}.json`,
        source: { sourceType: 'BYTE_CONTENT', byteContent: { data: base64(snapshot), mediaType: TENANT_WORKBOOK_MEDIA_TYPE } },
    }
}

function base64(bytes: Uint8Array): string {
    let text = ''
    for (let index = 0; index < bytes.length; index += 8192) {
        text += String.fromCharCode(...bytes.subarray(index, index + 8192))
    }
    return btoa(text)
}
